import {BasicEntity, BasicProperty} from "../../basic-entity-back/annotations";
import {ManagerCachingStrategy} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {BaseEntity} from "../base-entity.model";
import {PropertyType} from "../../basic-entity-back/property-type/property-type";
import {Cliente} from "../personas/cliente.model";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {ExactSearchFilter} from "../../basic-entity-back/filters/search-filter";
import {ArchivoBlender} from "../archivos/archivo-blender.model";
import {BlenderPrimaria} from "./blender-primaria.model";
import {BlenderSecundaria} from "./blender-secundaria.model";
import {ComentarioRevision} from "../solicitudes/comentario-revision.model";
import {ComentarioRevisionBlender} from "./comentario-revision-blender.model";
import {ArchivoBlenderDentalcad} from "../archivos/archivo-blender-dentalcad.model";
import {ArchivoBlenderHTML} from "../archivos/archivo-blender-html.model";

@BasicEntity({
    name: 'Blender',
    endpoint: 'blenders',
    modelNameProperty: 'resumen',
    isPaginated: true,
    managerCaching: ManagerCachingStrategy.Lazy,
    type: "Blender",
    // Un cliente solo ve sus propios blenders: filtrar por cliente en el panel no tiene sentido
    sinFiltroEnElPanel: ['cliente']
})
export class Blender extends BaseEntity {
    static readonly ESTADOS_BLENDER = {
        EN_CURSO: "Blender en curso",
        REALIZADO: "Blender realizado",
        RECHAZADO: "Blender rechazado",
        PENDIENTE_VALIDAR: "Pendiente de validación",
        A_REVISAR: "Diseño en revisión",
        VALIDADO: "Diseño validado"
    };
    @BasicProperty({
        isId: true,
        name: 'ID',
        type: PropertyType.Integer()
    })
    private _id: string;

    @BasicProperty({
        name: 'Estado',
        type: PropertyType.Choice(...Object.values(Blender.ESTADOS_BLENDER)),
        sortable: true,
        filters: [ExactSearchFilter]
    })
    private _estado;

    @BasicProperty({
        name: 'Cliente',
        type: PropertyType.NestedModel(Cliente, null, NestedBehavior.SendIriOnly),
        filters: [ExactSearchFilter]
    })
    private _cliente: Cliente;

    @BasicProperty({
        name: 'Código Paciente',
        type: PropertyType.String(3),
        nullable: false
    })
    private _codigoPaciente: string;

    @BasicProperty({
        name: 'Código Clínico (opcional)',
        type: PropertyType.String(),
        nullable: true
    })
    private _codigoClinico;

    @BasicProperty({
        name: 'Archivo',
        type: PropertyType.NestedModel(ArchivoBlender, 'blender', NestedBehavior.SendIriOnly)
    })
    private _archivo: ArchivoBlender;

    @BasicProperty({
        name: 'Archivo de visualización en Exocad',
        type: PropertyType.NestedModel(ArchivoBlenderHTML, 'blender', NestedBehavior.SendIriOnly),
        nullable: true
    })
    private _archivoBlenderHTML: ArchivoBlenderHTML;

    @BasicProperty({
        name: 'Archivo DENTALCAD',
        type: PropertyType.NestedModel(ArchivoBlenderDentalcad, 'blender', NestedBehavior.SendIriOnly),
        nullable: true
    })
    private _archivoDentalcad: ArchivoBlenderDentalcad;

    @BasicProperty({
        name: 'Primaria',
        type: PropertyType.NestedModel(BlenderPrimaria, 'blender', NestedBehavior.CreateOnly)
    })
    private _primaria: BlenderPrimaria;

    @BasicProperty({
        name: 'Secundaria',
        type: PropertyType.NestedModel(BlenderSecundaria, 'blender', NestedBehavior.CreateOnly)
    })
    private _secundaria: BlenderSecundaria;

    @BasicProperty({
        name: 'Solo diseño',
        type: PropertyType.Boolean()
    })
    private _soloDiseno = false;

    @BasicProperty({
        name: 'Observaciones',
        nullable: true,
        type: PropertyType.Text()
    })
    private _observaciones: string;

    @BasicProperty({
        name: 'Comentarios revisión',
        type: PropertyType.NestedModel(ComentarioRevisionBlender, 'blender', NestedBehavior.CreateOnly),
        array: true,
        nullable: true
    })
    private _comentariosRevision;

    get id(): string {
        return this._id;
    }

    set id(value: string) {
        this._id = value;
    }

    get cliente(): Cliente {
        return this._cliente;
    }

    set cliente(value: Cliente) {
        this._cliente = value;
    }

    get archivo(): ArchivoBlender {
        return this._archivo;
    }

    set archivo(value: ArchivoBlender) {
        this._archivo = value;
    }

    get primaria(): BlenderPrimaria {
        return this._primaria;
    }

    set primaria(value: BlenderPrimaria) {
        this._primaria = value;
    }

    get secundaria(): BlenderSecundaria {
        return this._secundaria;
    }

    set secundaria(value: BlenderSecundaria) {
        this._secundaria = value;
    }

    get soloDiseno(): boolean {
        return this._soloDiseno;
    }

    set soloDiseno(value: boolean) {
        this._soloDiseno = value;
    }

    get estado() {
        return this._estado;
    }

    set estado(value) {
        this._estado = value;
    }

    get codigoPaciente(): string {
        return this._codigoPaciente;
    }

    set codigoPaciente(value: string) {
        this._codigoPaciente = value;
    }

    get codigoClinico() {
        return this._codigoClinico;
    }

    set codigoClinico(value) {
        this._codigoClinico = value;
    }

    get observaciones(): string {
        return this._observaciones;
    }

    set observaciones(value: string) {
        this._observaciones = value;
    }

    get comentariosRevision() {
        return this._comentariosRevision;
    }

    set comentariosRevision(value) {
        this._comentariosRevision = value;
    }

    get archivoDentalcad(): ArchivoBlenderDentalcad {
        return this._archivoDentalcad;
    }

    set archivoDentalcad(value: ArchivoBlenderDentalcad) {
        this._archivoDentalcad = value;
    }

    get archivoBlenderHTML(): ArchivoBlenderHTML {
        return this._archivoBlenderHTML;
    }

    set archivoBlenderHTML(value: ArchivoBlenderHTML) {
        this._archivoBlenderHTML = value;
    }
}

BlenderPrimaria.BLENDER_MAP.type = PropertyType.Uri(BlenderPrimaria)
BlenderSecundaria.BLENDER_MAP.type = PropertyType.Uri(BlenderSecundaria)
ArchivoBlender.BLENDER_MAP.type = PropertyType.Uri(Blender)
ArchivoBlenderDentalcad.BLENDER_MAP.type = PropertyType.Uri(Blender)
ArchivoBlenderHTML.BLENDER_MAP.type = PropertyType.Uri(Blender)
ComentarioRevisionBlender.BLENDER_MAP.type = PropertyType.Uri(Blender)
